'use client';

import {useEffect, useState} from 'react';

export type DynamicFontArgs = {
	name: string;
	url: string;
};

function isValidFont(name: string) {
	const documentLoaded = typeof document !== 'undefined' && 'fonts' in document;
	let fontExists = false;

	if (documentLoaded) {
		for (const font of Array.from(document.fonts)) {
			if (!fontExists) {
				fontExists = font.family === name;
			}
		}
	}
	return fontExists;
}

// export maybe? seems useful everywhere
function isValidUrl(url: string) {
	try {
		const result = new URL(url);
		return result.protocol === 'http:' || result.protocol === 'https:';
	} catch (error) {
		console.error('The requested url is not valid.', {url, error});
		return false;
	}
}

async function loadFont(name: string, url: string) {
	try {
		const cssFriendlyUrl = `url(${url})`;
		const font = new FontFace(name, cssFriendlyUrl);
		const loaded = await font.load();
		document.fonts.add(loaded);
		return loaded;
	} catch (error) {
		console.error('Could not load font dynamically.', {name, url, error});
	}
}

/** A useEffect wrapper that makes sure dynamic fonts are added safely. Triggers re-render automatically. */
export function useDynamicFont({name, url}: DynamicFontArgs) {
	const [isLoaded, setIsLoaded] = useState(isValidFont(name));

	useEffect(() => {
		let canceled = false;

		if (!isValidUrl(url)) {
			return;
		}

		(async () => {
			await loadFont(name, url);

			if (!canceled) {
				setIsLoaded(true);
			}
		})();

		return () => {
			canceled = true; // this sets the internal state of the lambda and allow us to trigger a state-based re-render if the hook is still valid
		};
	}, [isLoaded, name, url]);

	return {isLoaded};
}
